import {FC, useEffect, useState, useCallback} from 'react'
import {GET, POST, PUT} from '../../../services/api'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Loading} from '../../../components/Loading/Loading'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import io from 'socket.io-client'
import useAuth from '../../../hooks/useAuth'
import styles from './index.module.css'
import {i18n} from '../../../translate/i18n'

const socket = io('https://zap-nis.ninegrid.com.br', {
  //const socket = io("http://localhost:7035", {
  transports: ['websocket', 'polling'],
  path: '/socket.io/',
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 5000,
})

const WhatsAppWebContent: FC = () => {
  const [loading] = useState(false)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [loadingQr, setLoadingQr] = useState(false)
  const [storeId, setStoreId] = useState<number | null>(null)
  const [statusMessage, setStatusMessage] = useState<string>('')
  const {loginInfo} = useAuth()
  const [isFood, setIsFood] = useState(false)
  const [sessionTimeout, setSessionTimeout] = useState<NodeJS.Timeout | null>(null)
  const [requestTimeout, setRequestTimeout] = useState<NodeJS.Timeout | null>(null)
  const [renderKey, setRenderKey] = useState(0)

  const init = useCallback(() => {
    if (loginInfo?.pluginspaceId) {
      setStoreId(loginInfo.pluginspaceId)
    }
  }, [loginInfo])

  // Função para verificar o tipo de pluginSpace
  const checkPluginSpaceType = useCallback(async () => {
    if (!storeId) return
    try {
      const response = await GET(`/pluginspace/${storeId}`)
      if (response.success && response.data) {
        setIsFood(response.data[0].type === 'food')
      }
    } catch (error) {
      console.error('Erro ao verificar tipo de pluginSpace:', error)
    }
  }, [storeId])

  useEffect(() => {
    init()
  }, [init])

  useEffect(() => {
    if (storeId) {
      checkPluginSpaceType()
    }
  }, [storeId, checkPluginSpaceType])

  // Função para resetar completamente o estado
  const resetSessionState = useCallback(
    (reason: string) => {
      console.log(`Resetando estado da sessão - Motivo: ${reason}`)

      // Limpa timeouts
      if (sessionTimeout) {
        clearTimeout(sessionTimeout)
        setSessionTimeout(null)
      }
      if (requestTimeout) {
        clearTimeout(requestTimeout)
        setRequestTimeout(null)
      }

      // Reset states
      setLoadingQr(false)
      setQrCode(null)

      // Força re-render
      setRenderKey((prev) => prev + 1)

      console.log('Estado resetado com sucesso')
    },
    [sessionTimeout, requestTimeout]
  )

  useEffect(() => {
    if (!storeId) return

    console.log('Configurando socket para storeId:', storeId)

    // Remove listeners existentes para evitar duplicação
    socket.off('qrcode')
    socket.off('session_ready')
    socket.off('mensage_status')
    socket.off('session_disconnect')

    // Join na sala com storeId como string (conforme esperado pelo backend)
    socket.emit('join', String(storeId))
    console.log('Enviando join para sala:', String(storeId))

    // Listener para QR Code - versão melhorada
    socket.on('qrcode', (data: {storeId: number; qrCode: string}) => {
      console.log('QR Code recebido do backend:', {
        receivedStoreId: data.storeId,
        currentStoreId: storeId,
        hasQrCode: !!data.qrCode,
        qrCodeLength: data.qrCode?.length,
        qrCodeStart: data.qrCode?.substring(0, 50),
      })

      if (Number(data.storeId) === storeId && data.qrCode) {
        try {
          setStatusMessage('Processando QR Code...')

          // Validação mais flexível do QR code
          const isValidQrCode = (qr: string): boolean => {
            // Verifica se é base64 com header
            if (/^data:image\/(png|jpeg|jpg|gif);base64,/.test(qr)) {
              return true
            }
            // Verifica se é base64 puro (sem header)
            if (/^[A-Za-z0-9+/=]+$/.test(qr) && qr.length > 100) {
              return true
            }
            return false
          }

          if (isValidQrCode(data.qrCode)) {
            // Se não tem header data:image, adiciona
            let processedQrCode = data.qrCode
            if (!data.qrCode.startsWith('data:image/')) {
              processedQrCode = `data:image/png;base64,${data.qrCode}`
            }

            console.log('QR Code válido, exibindo...')

            // Limpa ambos os timeouts pois QR code foi recebido com sucesso
            if (sessionTimeout) {
              clearTimeout(sessionTimeout)
              setSessionTimeout(null)
            }
            if (requestTimeout) {
              clearTimeout(requestTimeout)
              setRequestTimeout(null)
            }

            setQrCode(processedQrCode)
            setLoadingQr(false)
            setStatusMessage('Escaneie o QR Code no WhatsApp')
          } else {
            console.error('QR Code inválido:', {
              format: data.qrCode.substring(0, 50),
              length: data.qrCode.length,
            })
            setStatusMessage('QR Code recebido em formato inválido')
          }
        } catch (error) {
          console.error('Erro ao processar QR Code:', error)
          setStatusMessage('Erro ao processar QR Code')
        }
      } else {
        console.warn('QR Code rejeitado:', {
          storeIdMatch: Number(data.storeId) === storeId,
          hasQrCode: !!data.qrCode,
          receivedStoreId: data.storeId,
          currentStoreId: storeId,
        })
      }
    })

    // Função para verificar e criar plugin de mensageria
    const checkAndCreateMessagingPlugin = async () => {
      if (!storeId) return
      try {
        const pluginTemplateId = isFood ? 234 : 31
        const pluginTypeId = isFood ? 18 : 17

        // Primeiro verifica se já existe o plugin
        const checkResponse = await POST(`pluginconfig/filter?take=1000`, {
          plugintemplateId: pluginTemplateId,
        })

        if (!checkResponse.data || checkResponse.data.length === 0) {
          // Se não existir, cria o plugin
          const createData = {
            content: {
              storephone: '',
              able: '1',
            },
            name: 'WhatsApp Web',
            plugintemplateId: pluginTemplateId,
            plugintypeId: pluginTypeId,
          }

          const createResponse = await POST('/pluginconfig', createData)
          if (createResponse.success) {
            // setStatusMessage('Plugin de mensageria criado com sucesso')
            return
          } else {
            // setStatusMessage('Erro ao criar plugin de mensageria')
            return
          }
        }

        if (checkResponse.data && checkResponse.data.length > 0) {
          // Se existir, ativa o plugin
          const plugin = checkResponse.data.find(
            (p: any) => p.plugintemplateId.id === pluginTemplateId
          )
          if (plugin && plugin.content.able === '0') {
            const updateData = {
              content: {
                able: '1',
                storephone: plugin.content.storephone,
              },
              name: plugin.name,
              plugintemplateId: plugin.plugintemplateId.id,
              plugintypeId: plugin.plugintypeId,
            }
            const updateResponse = await PUT(`/pluginconfig/${plugin.id}`, updateData)
            if (updateResponse.success) {
              // setStatusMessage('Plugin de mensageria ativado com sucesso')
              return
            } else {
              // setStatusMessage('Erro ao ativar plugin de mensageria')
              return
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar/criar plugin:', error)
        setStatusMessage('Erro ao verificar plugin de mensageria')
      }
    }

    // QR Code escaneado e sessão conectada
    socket.on('session_ready', (data: {storeId: number}) => {
      console.log('Session ready recebido:', {
        receivedStoreId: data.storeId,
        currentStoreId: storeId,
        match: Number(data.storeId) === storeId,
      })

      if (Number(data.storeId) === storeId) {
        console.log('Sessão conectada, limpando QR code e timeouts')

        // Para IMEDIATAMENTE qualquer loading/timeout
        resetSessionState('Session Connected')

        setStatusMessage('Conectando...')
        setTimeout(() => {
          setStatusMessage(`Sessão conectada para a loja ${storeId}`)
          setIsConnected(true)
          setQrCode(null)
          // Verifica e cria o plugin quando a sessão estiver pronta
          checkAndCreateMessagingPlugin()
          setTimeout(() => setStatusMessage(''), 3000)
        }, 3000)
      }
    })

    // Status da Sessão
    socket.on('mensage_status', (data: any) => {
      if (Number(data.storeId) === storeId) {
        setStatusMessage(data.message)
        setTimeout(() => setStatusMessage(''), 3000)
      }
    })

    // Desconexão
    socket.on('session_disconnect', (data: {storeId: number}): void => {
      console.log('Session disconnect recebido:', {
        receivedStoreId: data.storeId,
        currentStoreId: storeId,
        match: Number(data.storeId) === storeId,
      })

      if (Number(data.storeId) === storeId) {
        console.log('Sessão desconectada, limpando timeouts')

        // Limpa ambos os timeouts pois sessão foi desconectada
        if (sessionTimeout) {
          clearTimeout(sessionTimeout)
          setSessionTimeout(null)
        }
        if (requestTimeout) {
          clearTimeout(requestTimeout)
          setRequestTimeout(null)
        }

        setStatusMessage('Desconectando...')
        setTimeout(() => {
          setStatusMessage(`A loja ${storeId} foi desconectada.`)
          setIsConnected(false)
          setTimeout(() => setStatusMessage(''), 3000)
        }, 3000)
      }
    })

    return () => {
      console.log('Limpando listeners do socket para storeId:', storeId)

      // Limpa os timeouts se existirem
      if (sessionTimeout) {
        clearTimeout(sessionTimeout)
        setSessionTimeout(null)
      }
      if (requestTimeout) {
        clearTimeout(requestTimeout)
        setRequestTimeout(null)
      }

      socket.off('qrcode')
      socket.off('session_ready')
      socket.off('mensage_status')
      socket.off('session_disconnect')
    }
  }, [storeId, isFood, sessionTimeout, requestTimeout, resetSessionState])

  // Criar Sessão
  const handleCreateSession = async () => {
    if (!storeId) return

    console.log('Iniciando handleCreateSession')
    console.log('Estado inicial - loadingQr:', loadingQr)

    // Limpa timeouts anteriores se existirem
    if (sessionTimeout) {
      clearTimeout(sessionTimeout)
      setSessionTimeout(null)
    }
    if (requestTimeout) {
      clearTimeout(requestTimeout)
      setRequestTimeout(null)
    }

    console.log('Definindo loadingQr = true')
    setQrCode(null)
    setLoadingQr(true)
    setIsConnected(false)
    setStatusMessage('Enviando solicitação ao backend...')

    console.log(' Estado após set - loadingQr deveria ser true')

    // Timeout para detectar problemas no backend (30 segundos)
    const reqTimeoutId = setTimeout(() => {
      console.warn(' Request Timeout: Backend demorou mais de 30 segundos para responder')
      resetSessionState('Request Timeout - 30s')
      setStatusMessage(
        ' Backend está demorando para responder. Verifique a conexão ou tente novamente.'
      )

      setTimeout(() => setStatusMessage(''), 6000)
    }, 30000) // 30 segundos

    setRequestTimeout(reqTimeoutId)

    // Timeout geral para QR code (2 minutos)
    const timeoutId = setTimeout(() => {
      console.warn(' Session Timeout: Sessão demorou mais de 2 minutos para ser criada')
      resetSessionState('Session Timeout - 2min')
      setStatusMessage(
        ' Tempo limite excedido! A solicitação demorou mais de 2 minutos. Verifique se o backend está funcionando e tente novamente.'
      )

      // Remove a mensagem após 8 segundos (mais tempo para ler)
      setTimeout(() => {
        setStatusMessage('')
      }, 8000)
    }, 120000) // 2 minutos

    setSessionTimeout(timeoutId)

    try {
      console.log('Criando sessão para storeId:', storeId)
      await POST(`https://nis.ninegrid.com.br/api/zapnis-web/create/${storeId}`)
      //await POST(`http://localhost:7004/api/zapnis-web/create/${storeId}`)

      console.log('Backend respondeu, aguardando QR code...')

      // Backend respondeu, limpa o timeout de request
      if (reqTimeoutId) {
        clearTimeout(reqTimeoutId)
        setRequestTimeout(null)
      }

      // Atualiza status para aguardar QR code
      setStatusMessage('Aguardando QR Code... (timeout em 2 min)')
    } catch (error) {
      console.error(' Erro ao criar sessão:', error)
      resetSessionState('Catch Error')
      setStatusMessage(' Erro ao criar sessão. Verifique se o backend está funcionando.')
      setTimeout(() => setStatusMessage(''), 5000)
    }
  }

  // Consultar Status da Sessão
  const checkSessionStatus = async () => {
    if (!storeId) return
    setStatusMessage('Verificando status...')

    try {
      const response = await GET(`https://nis.ninegrid.com.br/api/zapnis-web/status/${storeId}`)
      //const response = await GET(`http://localhost:7004/api/zapnis-web/status/${storeId}`);

      if (response.success) {
        setStatusMessage(response.message.message)
        setTimeout(() => setStatusMessage(''), 3000)
      }
    } catch {
      setStatusMessage('Erro ao consultar status.')
      setTimeout(() => setStatusMessage(''), 3000)
    }
  }

  // Desconectar Sessão
  const handleDisconnectSession = async () => {
    if (!storeId) return

    // Limpa ambos os timeouts se existirem
    if (sessionTimeout) {
      clearTimeout(sessionTimeout)
      setSessionTimeout(null)
    }
    if (requestTimeout) {
      clearTimeout(requestTimeout)
      setRequestTimeout(null)
    }

    setStatusMessage('Desconectando...')

    try {
      await GET(`https://nis.ninegrid.com.br/api/zapnis-web/disconnect/${storeId}`)
      //await GET(`http://localhost:7004/api/zapnis-web/disconnect/${storeId}`);
    } catch {
      setStatusMessage('Erro ao desconectar.')
      setTimeout(() => setStatusMessage(''), 3000)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>WhatsApp Web</span>
                <BreadCrumbs />
              </h3>
            </div>
            <div className={styles.bodyWpp}>
              <div className={styles.container}>
                <h2>{i18n.t(`whatsappweb.welcome`)}:</h2>
                <ol>
                  <li>{i18n.t(`whatsappweb.step1`)}.</li>
                  <li>{i18n.t(`whatsappweb.step2`)}.</li>
                  <li>{i18n.t(`whatsappweb.step3`)}.</li>
                  <li>{i18n.t(`whatsappweb.step4`)}.</li>
                </ol>

                {/* Debug info - remover em produção */}
                <div className='mt-3 p-3 bg-light rounded'>
                  <small>
                    <strong>Status Info:</strong>
                    <br />
                    Store ID: {storeId}
                    <br />
                    Socket Connected: {socket.connected ? 'Sim' : 'Não'}
                    <br />
                    Whatsapp Is Connected: {isConnected ? 'Sim' : 'Não'}
                    <br />
                    QR Code Available: {qrCode ? 'Sim' : 'Não'}
                    <br />
                    <strong style={{color: loadingQr ? 'red' : 'green'}}>
                      Loading QR: {loadingQr ? 'SIM (TRAVADO?)' : 'NÃO'}
                    </strong>
                    <br />
                    Session Timeout: {sessionTimeout ? 'Ativo' : 'Inativo'}
                    <br />
                    Request Timeout: {requestTimeout ? 'Ativo' : 'Inativo'}
                    <br />
                    Render Key: {renderKey}
                  </small>
                </div>
              </div>
              <div>
                {/* Exibição da mensagem de status */}
                {statusMessage && (
                  <div
                    className={`text-center mb-4 ${
                      statusMessage.includes('⚠️') ? 'alert alert-warning' : ''
                    }`}
                  >
                    <h3>{statusMessage}</h3>
                    {statusMessage.includes('Tempo limite excedido') && (
                      <p className='mt-2'>
                        <small>
                          A criação da sessão pode falhar por diversos motivos: sobrecarga do
                          servidor, problemas de rede ou indisponibilidade temporária do WhatsApp.
                          Aguarde alguns instantes e tente novamente.
                        </small>
                      </p>
                    )}
                  </div>
                )}

                {/* Loading indicator - só exibe se não estiver conectado */}
                {loadingQr && !qrCode && !isConnected && (
                  <div className='text-center mb-4'>
                    <div className='spinner-border' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                    <p className='mt-2'>Aguardando QR Code...</p>
                    {sessionTimeout && (
                      <small className='text-muted'>
                        <i className='fas fa-clock me-1'></i>
                        Timeout automático em 2 minutos
                      </small>
                    )}
                  </div>
                )}

                {/* Exibição do QR Code */}
                {!isConnected && qrCode && (
                  <div className='text-center mb-4'>
                    {/* <h3>Escaneie o QR Code para conectar</h3> */}
                    <img
                      src={qrCode}
                      alt='QR Code'
                      width='256'
                      height='256'
                      style={{border: '1px solid #ddd', borderRadius: '8px'}}
                      onError={(e) => {
                        console.error('Erro ao carregar imagem do QR Code:', e)
                        setStatusMessage('Erro ao exibir QR Code')
                      }}
                      onLoad={() => {
                        console.log('QR Code carregado com sucesso')
                      }}
                    />
                  </div>
                )}

                {/* Status quando conectado */}
                {isConnected && (
                  <div className='text-center mb-4'>
                    <div className='alert alert-success'>
                      <h4>✅ WhatsApp Conectado!</h4>
                      <p>Sua sessão está ativa e funcionando.</p>
                    </div>
                  </div>
                )}

                {/* Botões alinhados à direita */}
                <div className='d-flex justify-content-end mt-2'>
                  {!isConnected && (
                    <button
                      className='btn btn-primary me-2'
                      onClick={handleCreateSession}
                      disabled={loadingQr}
                    >
                      {loadingQr ? 'Criando...' : 'Criar Sessão'}
                    </button>
                  )}

                  {/* Botão de reset de emergência - só mostra se estiver loading sem QR code e NÃO conectado */}
                  {loadingQr && !qrCode && !isConnected && (
                    <button
                      className='btn btn-warning me-2'
                      onClick={() => resetSessionState('Reset Manual')}
                      title='Resetar estado se estiver travado'
                    >
                      Reset
                    </button>
                  )}

                  <button className='btn btn-info me-2' onClick={checkSessionStatus}>
                    Status da Sessão
                  </button>
                  <button className='btn btn-danger' onClick={handleDisconnectSession}>
                    Desconectar Sessão
                  </button>
                  {/* {isConnected && (
                    <button className='btn btn-danger' onClick={handleDisconnectSession}>
                      Desconectar Sessão
                    </button>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const WhatsAppWeb: FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={[{title: 'WhatsAppWeb', path: 'whatsappweb', isActive: true}]}>
        WhatsApp Web
      </PageTitle>
      <WhatsAppWebContent />
    </>
  )
}

export {WhatsAppWeb, WhatsAppWebContent}
